import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Navigation, Clock, Star, Wallet, 
  User, Menu, Car, CreditCard, Gift, X, ChevronLeft,
  Plus, Minus
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useRide } from '@/context/RideContext';
import { mapsService } from '@/services/mapsService';
import { paymentService } from '@/services/paymentService';

const PassengerHome: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { 
    pickup, destination, setPickup, setDestination, 
    stops, addStop, removeStop, estimatedPrice,
    requestRide, isSearching 
  } = useRide();

  const [showMenu, setShowMenu] = useState(false);
  const [showRidePanel, setShowRidePanel] = useState(false);
  const [selectedRideType, setSelectedRideType] = useState('economy');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [showStops, setShowStops] = useState(false);

  const rideTypes = [
    { id: 'economy', name: 'اقتصادي', icon: Car, price: estimatedPrice },
    { id: 'comfort', name: 'مريح', icon: Car, price: Math.round(estimatedPrice * 1.3) },
    { id: 'family', name: 'عائلي', icon: Car, price: Math.round(estimatedPrice * 1.5) },
    { id: 'premium', name: 'VIP', icon: Car, price: Math.round(estimatedPrice * 1.8) },
  ];

  const paymentMethods = [
    { id: 'cash', name: 'نقدي', icon: Wallet },
    { id: 'wallet', name: 'المحفظة', icon: CreditCard },
    { id: 'bankak', name: 'بنكك', icon: CreditCard },
    { id: 'fawry', name: 'فوري', icon: CreditCard },
  ];

  useEffect(() => {
    // Initialize map
    mapsService.initMap('map', { lat: 15.5007, lng: 32.5599 }, 14);
    
    // Get current location
    mapsService.getCurrentPosition().then((position) => {
      const loc = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      mapsService.centerOnPosition(loc);
      mapsService.addMarker('current', loc, { title: 'موقعي الحالي' });
    });
  }, []);

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    
    const result = await paymentService.validateCoupon(couponCode, estimatedPrice);
    if (result.valid && result.coupon) {
      const discountAmount = paymentService.calculateDiscount(estimatedPrice, result.coupon);
      setDiscount(discountAmount);
    } else {
      alert(result.message);
    }
  };

  const handleRequestRide = async () => {
    if (!pickup || !destination) {
      alert('الرجاء تحديد نقطة الانطلاق والوجهة');
      return;
    }
    
    try {
      const rideId = await requestRide(selectedRideType, paymentMethod);
      navigate(`/passenger/ride/${rideId}`);
    } catch (error) {
      console.error('Error requesting ride:', error);
    }
  };

  const finalPrice = rideTypes.find(t => t.id === selectedRideType)?.price || estimatedPrice;
  const totalPrice = finalPrice - discount;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => setShowMenu(true)}
            className="p-2 hover:bg-gray-100 rounded-xl"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">رحلة</h1>
          <div className="w-10" />
        </div>
      </header>

      {/* Map Container */}
      <div className="flex-1 relative">
        <div id="map" className="absolute inset-0" />
        
        {/* Location Input Panel */}
        <div className="absolute top-4 left-4 right-4 bg-white rounded-2xl shadow-lg p-4">
          {/* Pickup */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">نقطة الانطلاق</p>
              <input
                type="text"
                placeholder="حدد موقعك"
                className="w-full text-sm font-medium focus:outline-none"
                value={pickup?.address || ''}
                onChange={(e) => {
                  // In production, use geocoding
                  setPickup({
                    id: 'pickup',
                    name: 'موقعي',
                    address: e.target.value,
                    coordinates: { lat: 0, lng: 0 },
                  });
                }}
              />
            </div>
          </div>

          {/* Destination */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Navigation className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">الوجهة</p>
              <input
                type="text"
                placeholder="إلى أين؟"
                className="w-full text-sm font-medium focus:outline-none"
                value={destination?.address || ''}
                onChange={(e) => {
                  setDestination({
                    id: 'destination',
                    name: 'الوجهة',
                    address: e.target.value,
                    coordinates: { lat: 0, lng: 0 },
                  });
                }}
              />
            </div>
          </div>

          {/* Add Stop Button */}
          <button
            onClick={() => setShowStops(!showStops)}
            className="mt-3 text-primary-500 text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            إضافة توقف
          </button>

          {/* Stops */}
          {showStops && stops.length > 0 && (
            <div className="mt-3 space-y-2">
              {stops.map((stop, index) => (
                <div key={stop.id} className="flex items-center gap-2 bg-gray-50 rounded-xl p-2">
                  <span className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-xs">
                    {index + 1}
                  </span>
                  <span className="flex-1 text-sm">{stop.location.address}</span>
                  <button 
                    onClick={() => removeStop(stop.id)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Find Ride Button */}
          <button
            onClick={() => setShowRidePanel(true)}
            disabled={!pickup || !destination}
            className="w-full mt-4 bg-primary-500 text-white py-3 rounded-xl font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ابحث عن رحلة
          </button>
        </div>
      </div>

      {/* Ride Selection Panel */}
      {showRidePanel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white rounded-t-3xl w-full max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">اختر نوع الرحلة</h2>
                <button 
                  onClick={() => setShowRidePanel(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Ride Types */}
              <div className="space-y-3 mb-6">
                {rideTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedRideType(type.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-colors ${
                      selectedRideType === type.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                      <type.icon className="w-6 h-6 text-gray-600" />
                    </div>
                    <div className="flex-1 text-right">
                      <p className="font-bold">{type.name}</p>
                      <p className="text-sm text-gray-500">وصول خلال 5 دقائق</p>
                    </div>
                    <div className="text-left">
                      <p className="font-bold">{type.price} ج.س</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Payment Method */}
              <div className="mb-6">
                <p className="font-medium mb-3">طريقة الدفع</p>
                <div className="flex gap-2 overflow-x-auto">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors ${
                        paymentMethod === method.id
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <method.icon className="w-4 h-4" />
                      {method.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Coupon */}
              <div className="mb-6">
                <p className="font-medium mb-3">كود الخصم</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="أدخل كود الخصم"
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    className="bg-primary-500 text-white px-6 rounded-xl font-medium hover:bg-primary-600 transition-colors"
                  >
                    تطبيق
                  </button>
                </div>
                {discount > 0 && (
                  <p className="text-green-600 text-sm mt-2">
                    تم تطبيق خصم {discount} ج.س
                  </p>
                )}
              </div>

              {/* Price Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">سعر الرحلة</span>
                  <span>{finalPrice} ج.س</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between mb-2 text-green-600">
                    <span>الخصم</span>
                    <span>-{discount} ج.س</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>الإجمالي</span>
                  <span>{totalPrice} ج.س</span>
                </div>
              </div>

              {/* Request Button */}
              <button
                onClick={handleRequestRide}
                disabled={isSearching}
                className="w-full bg-primary-500 text-white py-4 rounded-xl font-bold hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSearching ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    جاري البحث...
                  </>
                ) : (
                  <>اطلب الرحلة الآن</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Side Menu */}
      {showMenu && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl">
            <div className="p-6">
              {/* User Info */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <p className="font-bold">{currentUser?.name || 'الراكب'}</p>
                  <p className="text-gray-500 text-sm" dir="ltr">{currentUser?.phone}</p>
                </div>
              </div>

              {/* Menu Items */}
              <nav className="space-y-2">
                <button 
                  onClick={() => { navigate('/passenger/profile'); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-right"
                >
                  <User className="w-5 h-5 text-gray-400" />
                  <span>الملف الشخصي</span>
                </button>
                <button 
                  onClick={() => { navigate('/passenger/wallet'); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-right"
                >
                  <Wallet className="w-5 h-5 text-gray-400" />
                  <span>المحفظة</span>
                </button>
                <button 
                  onClick={() => { navigate('/passenger/rides'); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-right"
                >
                  <Clock className="w-5 h-5 text-gray-400" />
                  <span>سجل الرحلات</span>
                </button>
                <button 
                  onClick={() => { navigate('/passenger/rides'); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-right"
                >
                  <Star className="w-5 h-5 text-gray-400" />
                  <span>التقييمات</span>
                </button>
              </nav>

              {/* Logout */}
              <button
                onClick={async () => {
                  await logout();
                  navigate('/');
                }}
                className="w-full mt-8 p-3 text-red-600 rounded-xl hover:bg-red-50 text-right"
              >
                تسجيل الخروج
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerHome;
